import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X as CloseIcon, AlertTriangle, CheckCircle, Info } from 'lucide-react';

/**
 * Lightweight global toast system. Bottom-center stack, auto-dismisses
 * after 4s (configurable), action button optional. Replaces silent
 * `.catch(() => {})` failures across the app — when something goes
 * wrong, the user sees what + has a retry option when relevant.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.error('Couldn\'t join the room', { onRetry: handleJoin });
 *   toast.success('Friend request sent');
 *   toast.info('Atom is thinking…');
 */

type ToastKind = 'error' | 'success' | 'info';

interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
  durationMs: number;
  onAction?: () => void;
  actionLabel?: string;
}

interface ToastContextValue {
  show: (msg: string, opts?: { kind?: ToastKind; durationMs?: number; onAction?: () => void; actionLabel?: string }) => void;
  error: (msg: string, opts?: { onRetry?: () => void; durationMs?: number }) => void;
  success: (msg: string, opts?: { durationMs?: number }) => void;
  info: (msg: string, opts?: { durationMs?: number }) => void;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastContextValue>({
  show: () => {},
  error: () => {},
  success: () => {},
  info: () => {},
  dismiss: () => {},
});

export function useToast(): ToastContextValue {
  return useContext(Ctx);
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const show: ToastContextValue['show'] = useCallback((msg, opts = {}) => {
    const id = nextId++;
    const entry: ToastEntry = {
      id,
      kind: opts.kind || 'info',
      message: msg,
      durationMs: opts.durationMs ?? 4000,
      onAction: opts.onAction,
      actionLabel: opts.actionLabel,
    };
    // A toast that appears at the bottom of a phone held at 45° is a toast
    // nobody sees. The two-tone C5→E5 is what makes them look. PERSONAL — it
    // is a message to THIS user, so it never chorusses around the room.
    void import('../../services/soundService').then(m => m.soundService.notify());
    setToasts(t => [...t, entry]);
    if (entry.durationMs > 0) {
      setTimeout(() => dismiss(id), entry.durationMs);
    }
  }, [dismiss]);

  const error: ToastContextValue['error'] = useCallback((msg, opts = {}) => {
    show(msg, {
      kind: 'error',
      durationMs: opts.durationMs ?? 6000,
      onAction: opts.onRetry,
      actionLabel: opts.onRetry ? 'Retry' : undefined,
    });
  }, [show]);

  const success: ToastContextValue['success'] = useCallback((msg, opts = {}) => {
    show(msg, { kind: 'success', durationMs: opts.durationMs ?? 3000 });
  }, [show]);

  const info: ToastContextValue['info'] = useCallback((msg, opts = {}) => {
    show(msg, { kind: 'info', durationMs: opts.durationMs ?? 3000 });
  }, [show]);

  return (
    <Ctx.Provider value={{ show, error, success, info, dismiss }}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'max(80px, env(safe-area-inset-bottom, 80px))', // sits above bottom nav
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: '0 16px',
          zIndex: 260,
          pointerEvents: 'none',
        }}
      >
        <AnimatePresence>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              initial={{ y: 20, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 8, opacity: 0, scale: 0.97 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              style={{
                pointerEvents: 'auto',
                width: '100%',
                maxWidth: 360,
                background: t.kind === 'error' ? 'var(--coral, #b94a3b)' : t.kind === 'success' ? 'var(--green, #16A34A)' : 'var(--ink-1, #16161a)',
                color: '#fff',
                borderRadius: 12,
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: '0 12px 36px -8px rgba(0,0,0,0.25)',
                fontFamily: 'var(--ui)',
                fontSize: 13,
              }}
            >
              {t.kind === 'error' && <AlertTriangle size={16} />}
              {t.kind === 'success' && <CheckCircle size={16} />}
              {t.kind === 'info' && <Info size={16} />}
              <span style={{ flex: 1, lineHeight: 1.4 }}>{t.message}</span>
              {t.onAction && t.actionLabel && (
                <button
                  type="button"
                  onClick={() => { t.onAction?.(); dismiss(t.id); }}
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {t.actionLabel}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  padding: 2,
                  display: 'inline-flex',
                }}
              >
                <CloseIcon size={14} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}
